import { createContext, useContext, useMemo, useSyncExternalStore } from "react";
import { resolveBinding, truthy } from "../model/expr";
import type { Store } from "../model/store";
import type { ActionRunner } from "../controller/runtime";
import type { SDUINode } from "../model/types";

export interface SDUIContextValue {
  store: Store;
  runner: ActionRunner;
  appId: string;
  /** Local template scope (item/index/row) pushed by list/table. */
  scope: Record<string, unknown>;
}

const Ctx = createContext<SDUIContextValue | null>(null);

export function SDUIProvider(props: { value: SDUIContextValue; children: React.ReactNode }) {
  return <Ctx.Provider value={props.value}>{props.children}</Ctx.Provider>;
}

export function useSDUI(): SDUIContextValue {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSDUI must be used inside <SDUIProvider>");
  return c;
}

/** Subscribe to the reactive store snapshot. SSR-safe (same snapshot server + client). */
export function useSnapshot(): Record<string, unknown> {
  const { store } = useSDUI();
  const sub = useMemo(() => store.subscribe.bind(store), [store]);
  const get = () => store.snapshot();
  return useSyncExternalStore(sub, get, get);
}

/** Resolve a binding against store snapshot + local scope (+ optional extra). */
export function useResolved(value: unknown, extra?: Record<string, unknown>): unknown {
  const snap = useSnapshot();
  const { scope } = useSDUI();
  return resolveBinding(value, { ...snap, ...scope, ...(extra ?? {}) });
}

/** Two-way input helper: reads from `bind` path (or `value`), writes on change. */
export function useInput(node: SDUINode) {
  const { store, runner, scope } = useSDUI();
  const snap = useSnapshot();
  const bindPath = node.bind as string | undefined;
  const value = bindPath !== undefined
    ? store.get(bindPath)
    : resolveBinding(node.value, { ...snap, ...scope });
  const onChange = (v: unknown) => {
    if (bindPath !== undefined) store.set(bindPath, v);
    if (node.onChange) void runner.run(node.onChange as never, { event: { value: v } });
  };
  return { value, onChange };
}
export { truthy };

/** Child provider that layers additional local scope (list/table rows). */
export function ScopeProvider(props: { extra: Record<string, unknown>; children: React.ReactNode }) {
  const parent = useSDUI();
  const value = useMemo<SDUIContextValue>(
    () => ({ ...parent, scope: { ...parent.scope, ...props.extra } }),
    [parent, props.extra],
  );
  return <SDUIProvider value={value}>{props.children}</SDUIProvider>;
}
